<template>
    <div class="card card-body rounded-corner" :class="editData?.id ? 'p-0 border-0' : ''">
        <form class="d-flex flex-column py-2 gap-y-1" method="POST" ref="form" :data-url="POSTS">
            <!-- body input -->
            <app-input type="textarea" v-model="formData.body" @input="typeChange" input-class="form-control"
                       :placeholder="$t('share_ideas')" @focus="focus" @blur="blur"
                       :disabled="loadingData"
                       :error-message="$errorMessage(errors, 'body')"
            />
            <div>
                <!-- show files -->
                <div class="d-flex flex-wrap" style="gap: 5px;">
                    <div v-for="(attachment, index) in formData.attachments"
                         :key="`attachment-item-${index}`"
                         :class="index === 0? 'preview': 'preview preview--sm'"
                         class="d-inline-block align-items-center d-flex position-relative">
                        <div class="w-100 h-100" v-html="sanitize(getHtmlContent(attachment))"></div>
                        <!-- remove current file -->
                        <button type="button" class="fab fab-red fab-transparent fab-2 position-absolute"  style="right: 10px; top: 10px; z-index: 100;"
                                @click="removeAttachments(index, attachment)">
                            <app-icon name="x" :size="16" />
                        </button>
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center justify-content-between gap-x-3">
                <div class="d-flex gap-x-2 align-items-center flex-fill">
                    <!-- emoji -->
                    <emoji-picker @emoji="insert" :search="search" >
                        <div slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }"
                             @click.stop="clickEvent">
                            <button type="button" class="btn btn-link p-0" @focus="focus" :disabled="loadingData" >
                                <span class="d-flex align-items-center gap-x-1">
                                    <app-icon name="smile" :size="18"/>
                                    <span class="">Emoji</span>
                                </span>
                            </button>
                        </div>
                        <div class="position-absolute card card-body p-2" style="width: 260px; z-index: 100;" slot="emoji-picker"
                             slot-scope="{ emojis, insert }">
                            <div>
                                <div>
                                    <input class="form-control-sm w-100" type="text" v-model="search" />
                                </div>
                                <div class="custom-scrollbar" style="height: 220px; overflow-y: scroll;">
                                    <div v-for="(emojiGroup, category) in emojis" :key="category">
                                        <small>{{ category }}</small>
                                        <div>
                                            <span class="cursor-pointer" style="font-size: 20px;" v-for="(emoji, emojiName) in emojiGroup"
                                                  :key="emojiName" @click="insert(emoji)" :title="emojiName">
                                                {{ emoji }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </emoji-picker>

                    <!-- add video button -->
<!--                    <button type="button" class="btn btn-link p-0"-->
<!--                            @click.prevent="addAttachment('.mp4, .avi')" :disabled="loadingData" >-->
<!--                        <span class="d-flex align-items-center gap-x-1">-->
<!--                            <app-icon name="film" :size="18"/>-->
<!--                            <span>Video</span>-->
<!--                        </span>-->
<!--                    </button>-->

                    <!-- add image button -->
                    <button type="button" class="btn btn-link p-0"
                            @click.prevent="addAttachment('.jpg, .png, .jpeg, .gif, .webp')" :disabled="loadingData" >
                        <span class="d-flex align-items-center gap-x-1">
                            <app-icon name="image" :size="18"/>
                            <span>Image</span>
                        </span>
                    </button>
                </div>
                <div>
                    <select class="form-control" v-model="formData.shared_with">
                        <option value="public" selected>{{ $t('public') }}</option>
                        <option value="team">{{ $t('team') }}</option>
                    </select>
                </div>
            </div>
            <div v-if="formData.shared_with === 'team'">
                <app-input type="multi-select" :list="teams" v-model="formData.teams" :disabled="loadingData" list-value-field="name" />
            </div>

            <div v-if="isFocus">
                <button class="btn btn-primary shadow-none btn-block rounded-pill" type="button" :disabled="loadingData" @click.prevent="submit">
                    <app-icon name="send" :size="14" class="mr-2"/>
                     {{ editData?.id ? $t('update') : $t('post') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import EmojiPicker from 'vue-emoji-picker'
import extractUrls from "extract-urls"
import FormHelperMixins from "../../../../mixins/global/FormHelperMixins";
import {POSTS} from "../../../../config/ApiUrl";
import { mapGetters } from 'vuex'
import {urlGenerator} from "../../../../helpers/AxiosHelper";
import sanitize from "../../../../helpers/SanitizeHelper";

export default {
    mixins: [FormHelperMixins],
    props: ['editData'],
    components: {
        EmojiPicker
    },
    data() {
        return {
            sanitize,
            isFocus: false,
            formData: {
                body: '',
                shared_with: 'public',
                attachments: [],
                teams: [],
                remove_attachments: [],
                // urls: [],
            },
            attachmentsToRemove: [],
            preview: '',
            maxFileSize: (150 * 1024 * 1024),
            search: '',
            POSTS,
        }
    },
    computed: {
        ...mapGetters({
            teams : 'teams/teams',
            loadingData: 'posts/loading'
        })
    },
    methods: {
        typeChange: _.debounce(function (body) {
            let urls = extractUrls(body) || []
            let formUrls = this.formData.attachments.filter(item => !(item instanceof File))

            for (let url of urls) {
                if(!formUrls.includes(url)){
                    this.formData.attachments.push(url)
                }
            }
        }, 500),
        getHtmlContent(data) {
            let isFile = data instanceof File
            let isImageUrl = (url) => ['png', 'gif', 'jpg', 'jpeg', 'webp'].includes(url.split('.').pop().toLowerCase())

            let isValidYoutubeUrl = (url) => {
                let p = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
                if (url.match(p)) {
                    return url.match(p)[1];
                }
                return false;
            }

            if (!isFile) {
                if (isImageUrl(data)) return `<img src="${data}" style="width: 100%;" alt="">`
                else if (isValidYoutubeUrl(data)) {
                    function getId(url) {
                        let regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                        let match = url.match(regExp);
                        if (match && match[2].length === 11) {
                            return match[2];
                        } else {
                            return 'error';
                        }
                    }

                    const videoId = getId(data);
                    return '<iframe width="100%" height="100%" src="//www.youtube.com/embed/' + videoId + '" frameborder="0" allowfullscreen></iframe>';
                }
            } else {
                let fileType = (data) => {
                    if (data.type.split('/')[0] === 'image') return 'image'
                    if (data.type.split('/')[0] === 'video') return 'video'
                }
                let url = URL.createObjectURL(data)
                if (fileType(data) === 'image') {
                    return `<img src="${url}" style="max-width: 100%; width:100%; height: 100%; max-height: 100%; object-fit: cover;" alt="">`
                }
                if (fileType(data) === 'video') {
                    return `<video height="100%"><source src="${url}" type="${data.type}"></video>`
                }
            }
        },
        submit() {
            let formData = new FormData();
            formData.append('body', this.formData.body)
            formData.append('shared_with', this.formData.shared_with)
            this.formData.attachments.forEach(file => {
                formData.append('attachments[]', file)
            })
            if(this.formData.shared_with === 'team'){
                this.formData.teams.forEach(team => {
                    formData.append('teams[]', team)
                })
            }
            if(this.editData) {
                formData.append('_method','patch')
                this.formData.remove_attachments.forEach(file => {
                    formData.append('remove_attachments[]', JSON.stringify(file))
                })
                this.$emit('update', formData)
                return
            }

            this.$store.dispatch('posts/create', formData).then(res => {
                this.formData = {
                    body: '',
                    shared_with: 'public',
                    attachments: [],
                    teams: [],
                    remove_attachments: [],
                    // urls: [],
                }
                this.attachmentsToRemove = []
                this.$toastr.s(res.data.message)
            }).catch(err => {
                this.errors = err.response.data.errors
                this.$toastr.e(err.response.data.message)
            }).finally(() => {
                this.fieldStatus.isSubmit = false;
            })
        },
        afterSuccess({data}) {
            this.formData = {
                body: '',
                shared_with: 'public',
                attachments: [],
                remove_attachments: [],
            };
            this.attachmentsToRemove = []
            // this.$toastr.s(data.message);
        },
        change(event) {
            let files = event.target.files
            for (let file of files) {
                if (file.size < this.maxFileSize) {
                    this.formData.attachments.push(file)
                } else {
                    this.$toastr.e(this.$t('file_size_exceeded'))
                }
            }
        },
        addAttachment(extensions) {
            this.focus()
            let input = document.createElement('input')
            input.type = 'file'
            input.multiple = true
            input.accept = extensions
            input.click();
            input.addEventListener('change', this.change)
        },
        removeAttachments(index, item) {
            let attachmentToRemove = this.attachmentsToRemove[index] || null;
            if(attachmentToRemove) {
                this.attachmentsToRemove.splice(index, 1);
                this.formData.remove_attachments.push(JSON.parse(JSON.stringify(attachmentToRemove)))
            }

            this.formData.attachments.splice(index, 1);
            if( typeof item === 'string'){
                function escapeRegExp(string) {
                    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                }
                function replaceAll(str, find, replace) {
                    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
                }
                this.formData.body = replaceAll(this.formData.body, item, '')
            }
        },
        emoji(e) {
            let keyboardEvent = new KeyboardEvent('keypress', {key: ' ', ctrlKey: true, metaKey: true})
            document.dispatchEvent(keyboardEvent);
        },
        insert(emoji) {
            this.focus()
            this.formData.body += emoji
        },
        focus(e) {
            this.isFocus = true
        },
        blur(e) {
            if (!this.formData.body && !this.formData.attachments.length) {
                this.isFocus = false
            }
        },
    },
    created() {
        // this.$store.commit('teams/SET_LIST', [])
        // this.$store.dispatch('teams/getAll')
        if(this.editData) {
            // get post then fill formData.
            axios.get(urlGenerator(POSTS)+`/${this.editData.id}`)
                .then((res) => {
                    this.formData = res.data;
                    this.attachmentsToRemove = JSON.parse(JSON.stringify(res.data.attachments))
                    this.formData.attachments = res.data.attachments.map(item => item.full_url)
                    this.formData.remove_attachments = []
                    this.formData.teams = res.data.teams.map(team => (team.id))
                    this.focus()
                })
        }
    }
}
</script>

<style scoped lang="scss">
.preview {
    width: 100%;
    height: 315px;
    object-fit: cover;
    overflow: hidden;
    &--sm{
       width: 32.6%;
       height: 120px;
    }
}
</style>